// interfaces/types
import { RefreshContractValidationState } from '@makemydeal/dr-dash-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import { changeVehicleSelectors } from '@makemydeal/dr-activities-change-vehicle';
import * as baseDealSelectors from '../selectors/BaseDealSelectors';
import * as vehicleProtectionSelectors from '../selectors/vehicleProtectionSelectors';

// utils
import { registerPendingOperationSelector } from './paymentSequenceRegistry';

/**
 * Every state-keyed pending operation the payment sequence guard must not discard a response out from
 * under, in one module.
 *
 * WHY THEY LIVE TOGETHER RATHER THAN BESIDE THEIR REACTORS
 * These registrations are the guard's fail-open safety net, so an unregistered operation hangs. Registering
 * from the reactor's own module would make the net depend on someone importing that module: in the dash app
 * both reactors are reachable only through `getRemainingMiddleware()`, while the guard is installed by
 * `getInitialMiddleware()`, which imports neither. Any host composing the guard without the rest of the
 * chain — a lighter shell, a slice-level integration test — would get an empty registry and therefore no
 * net at all, silently. `paymentSequenceMiddleware` imports this module, so installing the guard is what
 * populates the registry and the two cannot come apart.
 *
 * CALL THE SELECTORS, DO NOT RE-READ THE STATE
 * These read through `getRefreshContractValidationState` / `getCalcEngineTransitionState` rather than
 * touching `state.vehicleProtectionV2` / `state.baseDeal` directly. A hand-rolled copy of a one-line property
 * read looks harmless and fails in the worst possible direction: reshape the slice, update the selector, and
 * the copy quietly returns `undefined`, which `findPendingOperation` reads as "nothing pending" — so the
 * guard discards the response the operation was waiting on and the workflow hangs. That is precisely the
 * failure this registry exists to prevent, so there is only ever one reader per piece of state.
 *
 * WHEN TO ADD ONE
 * Only for a reactor that (a) sits downstream of the guard, (b) acts after its own `next()`, and (c)
 * discriminates on STATE rather than on the trigger's action type — so no `PAYMENT_SEQUENCE_EXEMPT_TRIGGERS`
 * entry can express it. Anything keyed on an action type belongs on the exempt list instead; anything
 * upstream of the guard needs neither. A selector registered here is consulted for EVERY superseded
 * response, so it must be true only during the narrow window its operation is actually in flight — a
 * broadly-true selector disables the guard globally for that duration. See `paymentSequenceRegistry.ts` and
 * the README in this directory.
 */

/**
 * `vehicleProtectionMiddleware`'s contract-validation refresh. `contractingMiddleware` puts it into PENDING
 * and the paths back out are that middleware's `PAYMENT_RECEIVED` (→ IDLE, for ANY payment, not just the one
 * that requested the refresh) and `PAYMENT_FAILURE` (→ PENDING_AFTER_FAILURE, still gated) cases, which
 * discriminate purely on this state — any payment at all can be the one that clears it, so no exempt entry
 * can express it and the AST scan in `paymentSequenceExemptList.spec.ts` cannot see it either.
 *
 * Without this, a superseded response is discarded before it reaches the middleware, PENDING is never
 * cleared, and `refreshContractValidationErrors()` never fires.
 *
 * THE WINDOW IS NOT PROVABLY BOUNDED. It closes the moment ANY `PAYMENT_RECEIVED` lands while this is true —
 * which in an active session is virtually every payment, not only the refresh's own — but the refresh call
 * itself goes through the same `fetch` as every other payment with no timeout or abort, so a dropped response
 * with no further payment triggered for the rest of the session would leave this selector true, and the guard
 * passed-through globally, until a reload. Adding a client-side timeout is out of scope here for the same
 * reason the `paymentFailed` gap in the middleware docstring is: it is a behavior change to the shared payment
 * fetch path, not to this guard. What is in scope, and already in place, is visibility: sustained
 * `PaymentSequencePassThrough` volume under `selectorName = 'refreshContractValidation'` (see the README's
 * "Rollout signal" section) is what a stuck session looks like from the outside, so the fail-open trade this
 * registry makes is observable even if this particular window is not bounded.
 */
registerPendingOperationSelector('refreshContractValidation', (state: StateTree) => {
    const refreshState = vehicleProtectionSelectors.getRefreshContractValidationState(state);
    return (
        refreshState === RefreshContractValidationState.PENDING ||
        refreshState === RefreshContractValidationState.PENDING_AFTER_FAILURE
    );
});

/**
 * The Balloon → UPQ fallback in dash `paymentMiddleware`. `attemptBalloonFallback` fires off `PAYMENT_FAILURE`
 * (and off a PQ `PAYMENT_RECEIVED` carrying no valid payment) purely on `balloonFallbackFinanceData` — any
 * payment at all can be the one that trips it, so no exempt entry can express it. Discard that response and
 * `triggerBalloonFallbackToUPQ()` never runs, so the deal never retries on UPQ and shows no payment at all.
 *
 * `balloonFallbackPending` — the flag the fallback sets on itself — is deliberately NOT part of this. Its
 * clearing hangs off the UPQ call the fallback makes, and that call is triggered by
 * `UPDATE_CURRENT_CALCULATION_ENDPOINT`, which is on `PAYMENT_SEQUENCE_EXEMPT_TRIGGERS`: its response can never
 * be discarded, so nothing was being protected. Including it was not free, either — a registered selector is
 * consulted for EVERY superseded response, so a flag that outlives its own round trip forwards unrelated stale
 * responses for that whole window and disables the guard globally rather than protecting this workflow.
 *
 * What is left is true only between saving the Finance data and the fallback firing, which is what keeps this
 * selector narrow enough to register at all.
 */
registerPendingOperationSelector('balloonFallback', (state: StateTree) => {
    // `getCalcEngineTransitionState` reads `state.baseDeal` unguarded, and the guard runs before that slice
    // exists. `findPendingOperation` treats a throw as "nothing pending", so a pre-bootstrap payment degrades
    // to a normal discard rather than an error — but reporting it here would be reading a throw as data, so
    // the pre-hydration case is answered explicitly instead.
    if (!state.baseDeal) return false;

    return Boolean(baseDealSelectors.getCalcEngineTransitionState(state)?.balloonFallbackFinanceData);
});

/**
 * AVO "Change" terminal quote (APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS): discard it and the change hangs.
 * State-keyed, not EXEMPT_TRIGGERS, since the same apply also seeds Add-New-Scenario (must stay guarded).
 * isChangeProcessing spans ANY change, so non-AVO changes pass-through too — redundant (SWITCH_VEHICLE is
 * exempt) but harmless: bounded by VEHICLE_CHANGE_SUCCESS/FAILURE.
 * try/catch: the change-vehicle slice is host-app-keyed and absent pre-hydration → fail-open ("nothing pending").
 */
registerPendingOperationSelector('changeVehicleTermsApply', (state: StateTree) => {
    try {
        return changeVehicleSelectors.isChangeProcessing(state as any);
    } catch {
        return false;
    }
});
